import { useState } from "react";
import { toast } from "sonner";
import { ImagePlus, Plus, Save } from "lucide-react";
import { api, errMsg } from "@/lib/api";
import { uploadImageInChunks } from "@/lib/chunkUpload";

const empty = { title: "", synopsis: "", genres: "", year: "", rating: "", status: "Ongoing", featured: false, cover_url: "", banner_url: "" };

const fromAnime = (a) => ({
  title: a.title, synopsis: a.synopsis || "", genres: (a.genres || []).join(", "), year: a.year ?? "", rating: a.rating ?? "",
  status: a.status || "Ongoing", featured: !!a.featured, cover_url: a.cover_url || "", banner_url: a.banner_url || "",
});

export const AnimeForm = ({ onCreated, initial = null, onSaved }) => {
  const editing = !!initial;
  const [form, setForm] = useState(editing ? fromAnime(initial) : empty);
  const [cover, setCover] = useState(null);
  const [banner, setBanner] = useState(null);
  const [busy, setBusy] = useState(false);
  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));
  const tid = (s) => (editing ? `edit-${s}` : s);

  const submit = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      const cover_url = cover ? (await uploadImageInChunks(cover)).url : form.cover_url;
      if (!cover_url) throw new Error("A cover image or URL is required");
      const banner_url = banner ? (await uploadImageInChunks(banner)).url : form.banner_url || cover_url;
      const payload = {
        ...form,
        cover_url,
        banner_url,
        genres: form.genres.split(",").map((g) => g.trim()).filter(Boolean),
        year: form.year ? Number(form.year) : null,
        rating: form.rating ? Number(form.rating) : null,
      };
      if (editing) {
        const r = await api.put(`/anime/${initial.id}`, payload);
        toast.success(`"${r.data.title}" updated`);
        onSaved?.(r.data);
      } else {
        const r = await api.post("/anime", payload);
        toast.success(`"${r.data.title}" created`);
        setForm(empty);
        setCover(null);
        setBanner(null);
        onCreated?.(r.data);
      }
    } catch (err) {
      toast.error(errMsg(err));
    } finally {
      setBusy(false);
    }
  };

  return (
    <form data-testid={tid("admin-anime-form")} onSubmit={submit} className={`flex flex-col gap-4 ${editing ? "" : "kp-glass p-7 kp-card-clip"}`}>
      {!editing && (
        <div>
          <span className="kp-eyebrow">Step 1</span>
          <h2 className="font-display font-bold text-xl mt-1">New Anime</h2>
        </div>
      )}
      <input data-testid={tid("anime-title-input")} className="kp-input" placeholder="Title *" value={form.title} onChange={(e) => set("title", e.target.value)} required />
      <textarea data-testid={tid("anime-synopsis-input")} className="kp-input min-h-[100px] resize-y" placeholder="Synopsis" value={form.synopsis} onChange={(e) => set("synopsis", e.target.value)} />
      <input data-testid={tid("anime-genres-input")} className="kp-input" placeholder="Genres (comma separated: Action, Fantasy)" value={form.genres} onChange={(e) => set("genres", e.target.value)} />
      <div className="grid grid-cols-3 gap-3">
        <input data-testid={tid("anime-year-input")} className="kp-input" type="number" placeholder="Year" value={form.year} onChange={(e) => set("year", e.target.value)} />
        <input data-testid={tid("anime-rating-input")} className="kp-input" type="number" step="0.1" min="0" max="10" placeholder="Rating" value={form.rating} onChange={(e) => set("rating", e.target.value)} />
        <select data-testid={tid("anime-status-select")} className="kp-input" value={form.status} onChange={(e) => set("status", e.target.value)}>
          <option>Ongoing</option>
          <option>Completed</option>
        </select>
      </div>
      <label className="flex items-center gap-3 border border-dashed border-white/15 hover:border-[#00F0FF]/60 transition-colors px-4 py-4 cursor-pointer text-sm">
        <ImagePlus size={16} className="text-[#00F0FF]" />
        <span className="truncate text-[#94A3B8]">{cover ? cover.name : editing ? "Choose a new cover image (optional)" : "Choose cover image (JPG/PNG/WebP)"}</span>
        <input data-testid={tid("anime-cover-file-input")} type="file" accept="image/*" className="hidden" onChange={(e) => setCover(e.target.files?.[0] || null)} />
      </label>
      <input data-testid={tid("anime-cover-url-input")} className="kp-input" placeholder="...or paste a cover image URL" value={form.cover_url} onChange={(e) => set("cover_url", e.target.value)} />
      <label className="flex items-center gap-3 border border-dashed border-white/15 hover:border-[#FF2A55]/60 transition-colors px-4 py-4 cursor-pointer text-sm">
        <ImagePlus size={16} className="text-[#FF2A55]" />
        <span className="truncate text-[#94A3B8]">{banner ? banner.name : "Choose wide banner image (hero / background) — optional"}</span>
        <input data-testid={tid("anime-banner-file-input")} type="file" accept="image/*" className="hidden" onChange={(e) => setBanner(e.target.files?.[0] || null)} />
      </label>
      <input data-testid={tid("anime-banner-url-input")} className="kp-input" placeholder="...or paste a banner image URL (defaults to cover)" value={form.banner_url} onChange={(e) => set("banner_url", e.target.value)} />
      <label className="flex items-center gap-2 text-sm text-[#94A3B8] cursor-pointer">
        <input data-testid={tid("anime-featured-checkbox")} type="checkbox" checked={form.featured} onChange={(e) => set("featured", e.target.checked)} className="accent-[#FF2A55]" />
        Feature in hero banner
      </label>
      <button data-testid={tid("anime-create-submit")} type="submit" disabled={busy} className="kp-btn kp-btn-primary justify-center disabled:opacity-60">
        {editing ? <Save size={14} /> : <Plus size={14} />} {busy ? "Saving..." : editing ? "Save Changes" : "Create Anime"}
      </button>
    </form>
  );
};
